import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { FormikProps } from "formik";

import Grid2 from "@mui/material/Grid2";
import { TypeUser } from "src/interface/enum/TypeUser";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "src/utilities";
import { useAuth } from "src/context/auth";
import { IAppUser } from "src/interface/IUser";
import { ListOfStatusGlobal } from "src/interface/enum/StatusGlobal";
import { IAutoCompleteLabelValue } from "src/interface/enum/GeneralType";
import { ITask } from "src/interface/ITask";
import NeededDesc from "./NeededDesc";
import NeededHistory from "./NeededHistory";
import { IFollowupSchedule } from "src/interface/IFollowupSchedule";
import { ITaskLog } from "src/interface/ITaskLog";

const LogForm = ({
  formik,
  taskData,
  schedulData,
  LogsData,
  isLoading,
}: {
  formik: FormikProps<any>;
  taskData?: ITask;
  schedulData?: IFollowupSchedule;
  LogsData: ITaskLog[];
  isLoading: boolean;
}) => {
  const { user } = useAuth();

  // لیست سبک کارمندان (بدون Include) برای dropdown
  const { data: EditorsData } = useQuery({
    queryKey: ["EditorsDataSimple"],
    queryFn: () =>
      fetch(API_URL + "user/getByRole/" + TypeUser.EDITOR + "/simple", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Accept: "application/json",
        },
      }).then((res) => res.json()),
  });
  const [getEditors, setEditors] = useState<IAutoCompleteLabelValue[]>([]);
  useEffect(() => {
    if (EditorsData) {
      setEditors(
        EditorsData.map((item: IAppUser) => ({
          label: item.fName + " " + item.lName,
          value: item.id,
          item: item,
        }))
      );
    }
  }, [EditorsData]);

  useEffect(() => {
    if (schedulData) {
      formik.setFieldValue(
        "userRef",
        getEditors.find((editor) => editor.item.userName === user?.userName)
          ?.value
      );
    }
  }, [schedulData, formik.values.scheduleStatus]);

  return (
    <>
      <Grid2 container spacing={1} marginY={1} columns={12}>
        <Grid2 size={taskData ? 6 : 12}>
          {(taskData && (
            <Autocomplete
              value={
                formik.values.statusTask != null
                  ? ListOfStatusGlobal.find(
                      (s) =>
                        s.value.toString() ===
                        formik.values.statusTask.toString()
                    ) || null
                  : null
              }
              onChange={(event: any, newValue: any) => {
                formik.setFieldValue(
                  "statusTask",
                  newValue != null ? newValue.value : undefined
                );
              }}
              getOptionDisabled={(option) =>
                option ===
                ListOfStatusGlobal.find((s) => s.value === taskData.statusTask)
              }
              size="small"
              options={ListOfStatusGlobal}
              fullWidth
              renderInput={(params) => (
                <TextField
                  error={
                    formik.touched.statusTask &&
                    Boolean(formik.errors.statusTask)
                  }
                  helperText={
                    ((formik.touched.statusTask &&
                      formik.errors.statusTask) as boolean) ||
                    `وضعیت فعلی: "${
                      ListOfStatusGlobal.find(
                        (s) => s.value === taskData.statusTask
                      )?.label
                    }" - می‌توانید تغییر دهید یا فقط توضیحات وارد کنید`
                  }
                  {...params}
                  label="وضعیت (اختیاری)"
                  placeholder="وضعیت (اختیاری)"
                />
              )}
            />
          )) || <></>}
          {(schedulData && (
            <Autocomplete
              value={
                formik.values.scheduleStatus != null
                  ? ListOfStatusGlobal.find(
                      (s) =>
                        s.value.toString() ===
                        formik.values.scheduleStatus.toString()
                    ) || null
                  : null
              }
              onChange={(event: any, newValue: any) => {
                formik.setFieldValue(
                  "scheduleStatus",
                  newValue != null ? newValue.value : undefined
                );
              }}
              getOptionDisabled={(option) =>
                option ===
                ListOfStatusGlobal.find(
                  (s) => s.value === schedulData?.lastTaskLog?.statusTask
                )
              }
              size="small"
              options={ListOfStatusGlobal}
              fullWidth
              renderInput={(params) => (
                <TextField
                  error={
                    formik.touched.scheduleStatus &&
                    Boolean(formik.errors.scheduleStatus)
                  }
                  helperText={
                    ((formik.touched.scheduleStatus &&
                      formik.errors.scheduleStatus) as boolean) ||
                    `وضعیت فعلی: "${
                      ListOfStatusGlobal.find(
                        (s) => s.value === schedulData?.lastTaskLog?.statusTask
                      )?.label || "تعریف نشده"
                    }" - می‌توانید تغییر دهید یا فقط توضیحات وارد کنید`
                  }
                  {...params}
                  label="وضعیت (اختیاری)"
                  placeholder="وضعیت (اختیاری)"
                />
              )}
            />
          )) || <></>}
        </Grid2>

        <Grid2 size={taskData ? 6 : 0}>
          {(taskData && (
            <Autocomplete
              value={
                getEditors.find(
                  (editor: any) => editor.value === formik.values.userRef
                ) || null
              }
              onChange={(event: any, newValue: any) => {
                formik.setFieldValue("userRef", newValue?.value || undefined);
              }}
              defaultValue={
                (getEditors.length > 0 &&
                  getEditors.find(
                    (editor) => editor.item.userName === user?.userName
                  )) ||
                null
              }
              size="small"
              options={
                getEditors.filter(
                  (editor: IAutoCompleteLabelValue) => editor.item.isActive
                ) || []
              }
              getOptionLabel={(option: IAutoCompleteLabelValue) =>
                option.label || ""
              }
              isOptionEqualToValue={(
                option: IAutoCompleteLabelValue,
                value: IAutoCompleteLabelValue
              ) => option.value === value.value}
              renderOption={(props: any, option: IAutoCompleteLabelValue) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              fullWidth
              renderInput={(params) => (
                <TextField
                  error={
                    formik.touched.userRef && Boolean(formik.errors.userRef)
                  }
                  helperText={
                    ((formik.touched.userRef &&
                      formik.errors.userRef) as boolean) ||
                    `کارمند فعلی: ${taskData?.user?.fName || ""} ${taskData?.user?.lName || ""} - می‌توانید تغییر دهید`
                  }
                  {...params}
                  label="کارمند مرتبط (اختیاری)"
                  placeholder="کارمند مرتبط (اختیاری)"
                />
              )}
            />
          )) || <></>}
        </Grid2>
      </Grid2>
      <Grid2 container spacing={1} marginBottom={1}>
        <Grid2 size={12}>
          <NeededDesc
            formik={formik}
            taskData={taskData}
            schedulData={schedulData}
            LogsData={LogsData}
          />
        </Grid2>
      </Grid2>

      <Grid2 container spacing={1} marginBottom={1} width={673}>
        <NeededHistory
          LogsData={LogsData}
          getEditors={getEditors}
          isLoading={isLoading}
        />
      </Grid2>
    </>
  );
};

export default LogForm;
